import { Object3D, AnimationClip } from "three";
import type {
  ResourceType,
  LoadedResource,
  LoadedFile,
  SupportedFormat,
  ResourceDataType
} from './type';
import { getFileFormat } from './util';

/**
 * ResourceType을 생성하는 헬퍼 함수입니다.
 */
export function createResourceType(
  name: string,
  path: string,
  type?: ResourceDataType,
  options?: {
    extractAnimations?: boolean;
    extractModel?: boolean;
  }
): ResourceType {
  const format = getFileFormat(path);
  if (!format) {
    throw new Error(`Unsupported file format: ${path}`);
  }

  const resourceType: ResourceType = {
    name,
    path,
    format,
    type: type || 'model_only',
    extractAnimations: options?.extractAnimations ?? false,
    extractModel: options?.extractModel ?? true
  };

  return resourceType;
}

/**
 * GLTF 파일에서 애니메이션을 추출합니다.
 */
export function extractAnimationsFromGLTF(gltf: LoadedFile): AnimationClip[] {
  return gltf.animations || [];
}

/**
 * GLTF 파일에서 모델을 추출합니다.
 */
export function extractModelFromGLTF(gltf: LoadedFile): Object3D {
  return gltf.scene || gltf.scenes?.[0] || new Object3D();
}

/**
 * FBX 파일에서 애니메이션을 추출합니다.
 */
export function extractAnimationsFromFBX(fbx: LoadedFile): AnimationClip[] {
  return fbx.animations || [];
}

/**
 * FBX 파일에서 모델을 추출합니다.
 */
export function extractModelFromFBX(fbx: LoadedFile): Object3D {
  return fbx.scene || new Object3D();
}

/**
 * VRM 파일에서 애니메이션을 추출합니다.
 */
export function extractAnimationsFromVRM(vrm: LoadedFile): AnimationClip[] {
  return vrm.animations || [];
}

/**
 * VRM 파일에서 모델을 추출합니다.
 */
export function extractModelFromVRM(vrm: LoadedFile): Object3D {
  return vrm.scene || new Object3D();
}

/**
 * VRMA 파일에서 애니메이션을 추출합니다.
 */
export function extractAnimationsFromVRMA(vrma: LoadedFile): AnimationClip[] {
  // VRMA 파일의 경우 userData.vrmAnimations에서 애니메이션을 가져옵니다
  if (vrma.userData?.vrmAnimations && Array.isArray(vrma.userData.vrmAnimations)) {
    return vrma.userData.vrmAnimations as AnimationClip[];
  }
  return vrma.animations || [];
}

/**
 * VRMA 파일에서 모델을 추출합니다.
 */
export function extractModelFromVRMA(vrma: LoadedFile): Object3D {
  return vrma.scene || new Object3D();
}

/**
 * 로딩된 파일에서 데이터를 추출합니다.
 */
export function extractDataFromResource(
  resource: ResourceType,
  loadedFile: LoadedFile
): LoadedResource {
  const { type, format, extractAnimations, extractModel } = resource;

  let animations: AnimationClip[] = [];
  let model: Object3D | undefined = undefined;

  // 파일 형식에 따른 데이터 추출
  switch (format) {
    case 'gltf':
    case 'glb':
      if (extractAnimations) {
        animations = extractAnimationsFromGLTF(loadedFile);
      }
      if (extractModel) {
        model = extractModelFromGLTF(loadedFile);
      }
      break;

    case 'fbx':
      if (extractAnimations) {
        animations = extractAnimationsFromFBX(loadedFile);
      }
      if (extractModel) {
        model = extractModelFromFBX(loadedFile);
      }
      break;

    case 'vrm':
      if (extractAnimations) {
        animations = extractAnimationsFromVRM(loadedFile);
      }
      if (extractModel) {
        model = extractModelFromVRM(loadedFile);
      }
      break;

    case 'vrma':
      if (extractAnimations) {
        animations = extractAnimationsFromVRMA(loadedFile);
      }
      if (extractModel) {
        model = extractModelFromVRMA(loadedFile);
      }
      break;
  }

  return {
    file: loadedFile,
    type,
    format,
    animations: animations.length > 0 ? animations : undefined,
    model
  };
}

/**
 * 로딩된 파일을 표준 LoadedFile 형태로 변환합니다.
 */
export function normalizeLoadedFile(loadedFile: unknown, format: SupportedFormat): LoadedFile {
  const normalized: LoadedFile = {
    scene: undefined,
    animations: [],
    scenes: undefined,
    asset: undefined,
    userData: undefined
  };

  if (!loadedFile || typeof loadedFile !== 'object') {
    return normalized;
  }

  const file = loadedFile as Record<string, unknown>;

  switch (format) {
    case 'gltf':
    case 'glb':
      // GLTF 형태의 파일
      normalized.scene = file.scene as Object3D;
      normalized.animations = (file.animations as AnimationClip[]) || [];
      normalized.scenes = (file.scenes as Object3D[]) || [];
      normalized.asset = file.asset as LoadedFile['asset'];
      break;

    case 'fbx':
      // FBX 형태의 파일 (Object3D 자체)
      normalized.scene = file as unknown as Object3D;
      normalized.animations = (file.animations as AnimationClip[]) || [];
      break;

    case 'vrm':
      // VRM 형태의 파일
      normalized.scene = file.scene as Object3D;
      normalized.animations = (file.animations as AnimationClip[]) || [];
      normalized.userData = file.userData as Record<string, unknown>;
      break;

    case 'vrma':
      // VRMA 형태의 파일
      normalized.scene = file.scene as Object3D;
      normalized.animations = (file.animations as AnimationClip[]) || [];
      normalized.userData = file.userData as Record<string, unknown>;
      break;
  }

  return normalized;
} 